'use client'

import { ChevronDown, Menu, X } from 'lucide-react'
import * as React from 'react'
import { createPortal } from 'react-dom'

import { useAppBarState } from '../app-bar-state'
import { cn } from '../cn'
import { useHideOnScroll } from '../hooks/use-hide-on-scroll'
import { springEasing } from '../spring-easing'
import { AppLauncherTile, shouldShowAppBar, useAppBarApps } from './app-bar'
import { useShellChrome, useSidebarState } from './app-shell'
import { NotificationBellUnavailable } from './notification-center'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

/**
 * The unified shell's round 56pt buttons: menu, bell, search and +. Exported
 * so hosts can give their own trigger (e.g. a notification bell) the same
 * look.
 */
const SHELL_SURFACE_CLASS =
  'relative flex h-14 shrink-0 items-center justify-center rounded-full border border-border bg-background/80 text-foreground shadow-sm backdrop-blur-xl transition-[transform,background-color] hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'

export const SHELL_CIRCLE_CLASS = cn(SHELL_SURFACE_CLASS, 'w-14 active:scale-95 [&_svg]:h-[22px] [&_svg]:w-[22px]')

/**
 * The row's return: a spring that dips a few pixels past its spot and settles
 * ("Settle", PROD-1524). Leaving keeps the plain 300ms ease-out slide and fade.
 */
const SETTLE = springEasing({ stiffness: 260, damping: 22 })
// Timing only: the class owns `transition-property` (translate, then opacity),
// so `motion-reduce:transition-none` still switches the motion off.
const SETTLE_STYLE: React.CSSProperties = {
  transitionTimingFunction: `${SETTLE.easing}, cubic-bezier(0, 0, 0.2, 1)`,
  transitionDuration: `${SETTLE.durationMs}ms, 200ms`,
}

export interface MobileTopRowProps {
  /** Id of the current app in the HQ registry. Defaults to the id the sidebar publishes. */
  currentAppId?: string
  /** Apps without sidebar navigation keep the app switcher and bell only. */
  showMenu?: boolean
  /**
   * Right-hand slot, normally the app's notification bell (style it with
   * `SHELL_CIRCLE_CLASS`). Omitted, it shows a greyed-out bell so every app
   * keeps the bell in the same place.
   */
  trailing?: React.ReactNode
  className?: string
}

/**
 * Phone-only top row for the unified shell (`<AppShell layout="unified">`):
 * menu (opens the sidebar drawer), the current app (drops the app grid below
 * the row), and a trailing slot for the bell. It floats over the page and
 * slides away on scroll down, back on scroll up. Users with access to a
 * single app get no app button — the middle stays empty.
 */
export function MobileTopRow({ currentAppId, showMenu = true, trailing, className }: MobileTopRowProps) {
  const hidden = useHideOnScroll()
  const sidebar = useSidebarState()
  const { appGridOpen } = useShellChrome()
  const menuOpen = Boolean(sidebar?.open)
  const away = hidden && !appGridOpen && !menuOpen
  return (
    <>
      {/* Solid strip behind the status bar when installed to the home screen
          (viewport-fit=cover), so content never scrolls under the clock. */}
      <div
        aria-hidden
        className="fixed inset-x-0 top-0 z-40 h-[env(safe-area-inset-top,0px)] bg-background md:hidden"
      />
      <div
        data-shell-top-row
        className={cn(
          // Tailwind 4 moves things with `translate`, not `transform`, so that is
          // the property to animate; on `transform` the slide never played.
          'fixed inset-x-4 top-[calc(env(safe-area-inset-top,0px)+0.75rem)] z-40 flex items-center gap-2.5 transition-[translate,opacity] duration-300 ease-out motion-reduce:transition-none md:hidden',
          away
            ? 'pointer-events-none -translate-y-[calc(100%+env(safe-area-inset-top,0px)+1rem)] opacity-0'
            : 'translate-y-0 opacity-100',
          className,
        )}
        style={away ? undefined : SETTLE_STYLE}>
        {/* Turns into an X while the menu is open. In reveal mode the app is
            inert then, and the shell's close layer over the exposed edge takes
            the tap, so the X closes the menu as it appears to (PROD-1530). */}
        {showMenu && (
          <button
            type="button"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => sidebar?.setOpen(!menuOpen)}
            className={SHELL_CIRCLE_CLASS}>
            <Menu
              className={cn(
                'absolute transition-[transform,opacity] duration-300 motion-reduce:transition-none',
                menuOpen ? 'rotate-90 scale-50 opacity-0' : 'opacity-100',
              )}
            />
            <X
              className={cn(
                'absolute transition-[transform,opacity] duration-300 motion-reduce:transition-none',
                menuOpen ? 'opacity-100' : '-rotate-90 scale-50 opacity-0',
              )}
            />
          </button>
        )}
        <MobileAppSwitcher currentAppId={currentAppId} />
        <div className="ml-auto shrink-0">
          {trailing ?? <NotificationBellUnavailable triggerClassName={SHELL_CIRCLE_CLASS} />}
        </div>
      </div>
    </>
  )
}

function MobileAppSwitcher({ currentAppId: currentAppIdProp }: { currentAppId?: string }) {
  const { entries, isLoading } = useAppBarApps()
  const { currentAppId: publishedAppId } = useAppBarState()
  const { appGridOpen, setAppGridOpen } = useShellChrome()
  const currentAppId = currentAppIdProp ?? publishedAppId
  // `hideUntilLoaded`: a single-app user must never see a flash of a button
  // that leads nowhere.
  if (!shouldShowAppBar(entries.length, isLoading, true)) {
    return <div className="flex-1" />
  }
  const current = entries.find((entry) => entry.app.id === currentAppId)?.app
  return (
    <Popover open={appGridOpen} onOpenChange={setAppGridOpen}>
      <PopoverTrigger
        className={cn(SHELL_SURFACE_CLASS, 'min-w-0 flex-1 gap-2.5 px-4 text-[17px] font-semibold active:scale-[0.98]')}
        aria-label={current ? `${current.name}, switch app` : 'Switch app'}>
        {current ? <AppLauncherTile app={current} size="md" /> : null}
        <span className="min-w-0 truncate">{current?.name ?? 'Apps'}</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', appGridOpen && 'rotate-180')}
        />
      </PopoverTrigger>
      {appGridOpen && typeof document !== 'undefined'
        ? createPortal(<div aria-hidden className="fixed inset-0 z-30 bg-black/30 md:hidden" />, document.body)
        : null}
      <PopoverContent
        side="bottom"
        align="center"
        sideOffset={10}
        collisionPadding={8}
        className="z-modal w-[calc(100vw-16px)] max-w-[calc(100vw-16px)] rounded-[28px] px-3 py-5">
        <nav aria-label="Apps">
          <ul className="grid grid-cols-4 gap-x-1 gap-y-4">
            {entries.map(({ app }) => {
              const active = app.id === currentAppId
              return (
                <li key={app.id}>
                  <a
                    href={app.href}
                    aria-current={active ? 'page' : undefined}
                    onClick={() => setAppGridOpen(false)}
                    className="flex flex-col items-center gap-1.5 rounded-xl p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <AppLauncherTile
                      app={app}
                      size="lg"
                      current={active}
                      className={active ? 'ring-offset-popover' : undefined}
                    />
                    <span
                      className={cn(
                        'max-w-full truncate text-xs',
                        active ? 'font-semibold text-foreground' : 'text-muted-foreground',
                      )}>
                      {app.name}
                    </span>
                  </a>
                </li>
              )
            })}
          </ul>
        </nav>
      </PopoverContent>
    </Popover>
  )
}
